'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ViewContainer from '../layouts/ViewContainer';
import { useTelegram } from '@/providers/TelegramProvider';
import { TextField, InputAdornment, IconButton, Snackbar, Alert } from '@mui/material';

interface PaymentStatusViewProps {
    paymentId: string;
    onBack: () => void;
    paymentUrl?: string;
}

export default function PaymentStatusView({ paymentId, onBack, paymentUrl }: PaymentStatusViewProps) {
    const { refreshUser } = useTelegram(); // Убираем webApp из деструктуризации
    const [status, setStatus] = React.useState<'pending' | 'succeeded' | 'canceled' | 'error'>('pending');
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');

    React.useEffect(() => {
        let intervalId: NodeJS.Timeout;

        const checkStatus = async () => {
            try {
                const response = await fetch(`/payment/status/${paymentId}`);
                const data = await response.json();

                if (data.status === 'succeeded') {
                    setStatus('succeeded');
                    await refreshUser();
                    clearInterval(intervalId);
                } else if (data.status === 'canceled') {
                    setStatus('canceled');
                    clearInterval(intervalId);
                }
            } catch (e) {
                console.error("Check status error", e);
            }
        };

        checkStatus();
        intervalId = setInterval(checkStatus, 3000);

        const timeoutId = setTimeout(() => {
            clearInterval(intervalId);
            if (status === 'pending') {
                setStatus('error');
            }
        }, 5 * 60 * 1000);

        return () => {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };
    }, [paymentId, refreshUser, status]);

    const handleOpenPayment = () => {
        if (!paymentUrl) return;

        try {
            // Открываем в браузере, а не в Web App
            const link = document.createElement('a');
            link.href = paymentUrl;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setSnackbarMessage('Страница оплаты открыта в браузере');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error opening payment link:', error);
            // Запасной вариант
            window.open(paymentUrl, '_blank');
        }
    };

    const handleCopyLink = async () => {
        if (!paymentUrl) return;

        try {
            await navigator.clipboard.writeText(paymentUrl);
            setSnackbarMessage('Ссылка скопирована в буфер обмена');
            setSnackbarOpen(true);
        } catch (err) {
            setSnackbarMessage('Не удалось скопировать ссылку');
            setSnackbarOpen(true);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    return (
        <ViewContainer title="Статус оплаты" onClose={onBack}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                gap: 3,
                p: 3
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 3,
                    textAlign: 'center'
                }}>
                    {status === 'pending' && (
                        <>
                            <CircularProgress size={60} />
                            <Typography variant="h6">Ожидание оплаты...</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Пожалуйста, завершите оплату в открывшемся окне.
                            </Typography>
                        </>
                    )}

                    {status === 'succeeded' && (
                        <>
                            <CheckCircleIcon color="success" sx={{ fontSize: 80 }} />
                            <Typography variant="h5" color="success.main">Оплата прошла успешно!</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Средства зачислены на ваш баланс.
                            </Typography>
                            <Button variant="contained" fullWidth onClick={onBack}>
                                Вернуться в меню
                            </Button>
                        </>
                    )}

                    {(status === 'canceled' || status === 'error') && (
                        <>
                            <ErrorIcon color="error" sx={{ fontSize: 80 }} />
                            <Typography variant="h5" color="error.main">
                                {status === 'canceled' ? 'Оплата отменена' : 'Время ожидания истекло'}
                            </Typography>
                            <Button variant="outlined" fullWidth onClick={onBack}>
                                Попробовать снова
                            </Button>
                        </>
                    )}
                </Box>

                {status === 'pending' && paymentUrl && (
                    <Box sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                            🔄 Не открывается страница оплаты?
                        </Typography>

                        <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<OpenInNewIcon />}
                            onClick={handleOpenPayment}
                            sx={{ mb: 2 }}
                        >
                            Открыть страницу оплаты
                        </Button>

                        <TextField
                            fullWidth
                            size="small"
                            value={paymentUrl}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            onClick={handleCopyLink}
                                            size="small"
                                        >
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            variant="outlined"
                            placeholder="Ссылка на оплату"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Скопируйте ссылку и откройте в браузере
                        </Typography>
                    </Box>
                )}
            </Box>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </ViewContainer>
    );
}